import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import {
  ExpenseApprovalPolicyFields,
  type ExpensePolicyReferenceData,
} from "@/components/ExpenseApprovalPolicyFields";
import {
  draftFromPolicy,
  emptyDraft,
  toUpsertRequest,
  validateDraft,
  versionLabel,
  type ExpenseApprovalPolicyResponse,
  type ExpensePolicyDraft,
} from "@/lib/expense-approval-policy-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

export interface ExpenseApprovalPolicyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this policy (PUT); otherwise it creates one (POST). */
  policy?: ExpenseApprovalPolicyResponse | null;
  /** Default priority for a new policy (e.g. one past the current max). */
  defaultPriority?: number;
  /** Category, merchant, card and member lookups, from `useExpensePolicyReferenceData`. */
  referenceData: ExpensePolicyReferenceData;
  onSaved: () => void;
}

export function ExpenseApprovalPolicyFormDialog({
  open,
  onOpenChange,
  policy,
  defaultPriority = 100,
  referenceData,
  onSaved,
}: ExpenseApprovalPolicyFormDialogProps) {
  const api = useDolfinClient();
  const isEdit = !!policy;
  const [draft, setDraft] = useState<ExpensePolicyDraft>(() => emptyDraft(defaultPriority));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setDraft(policy ? draftFromPolicy(policy) : emptyDraft(defaultPriority));
  }, [open, policy, defaultPriority]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const problem = validateDraft(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    const body = toUpsertRequest(draft);

    setSubmitting(true);
    const { error: apiError } = isEdit
      ? await api.PUT("/v1/expenses/approval-policies/{id}", {
          params: { path: { id: policy!.id } },
          body,
        })
      : await api.POST("/v1/expenses/approval-policies", { body });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to save the expense policy.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    toast.success(isEdit ? "Expense policy updated." : "Expense policy created.");
    onOpenChange(false);
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? `Edit expense policy` : "New expense policy"}
              {isEdit ? (
                <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                  {versionLabel(policy!.version)}
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Policies are checked when a card expense is coded, before it posts. A policy for particular merchants,
              cards or people wins over one for all spend; after that, the lower priority number wins.
              {isEdit ? " Saving creates a new version." : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <ExpenseApprovalPolicyFields
              draft={draft}
              onChange={setDraft}
              referenceData={referenceData}
              disabled={submitting}
            />
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              {isEdit ? "Save changes" : "Create policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
